/**
 * Module dependencies.
 */

import { AuthField, AuthShell, AuthSubmit } from 'app/components/pages/auth/auth-shell';
import { MailCheck } from 'lucide-react';
import { Logo } from 'app/components/pages/landing/brand';
import { PasswordRequirements, allPasswordRulesMet } from 'app/components/pages/auth/password-requirements';
import { appRoutes } from 'app/routes';
import { cookieStorage } from 'app/core/utils/cookie-storage';
import { useNavigate } from 'react-router-dom';
import { useRegister } from 'app/hooks/requests/use-register';
import { useResendVerification } from 'app/hooks/requests/use-resend-verification';
import React, { useEffect, useState } from 'react';

/**
 * Export `RegisterPage` component.
 */

export function RegisterPage(): JSX.Element {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [resent, setResent] = useState(false);

  const { mutate: register, isLoading } = useRegister({
    onError: () => setError('Could not create your account. That email may already be in use.'),
    onSuccess: () => setSubmitted(true)
  });

  const { mutate: resend } = useResendVerification({ onSuccess: () => setResent(true) });

  useEffect(() => {
    if (cookieStorage.get('user')) {
      navigate(appRoutes.dashboard.home.base);
    }
  }, [navigate]);

  const allRulesMet = allPasswordRulesMet(password);
  const passwordsMatch = confirm.length > 0 && confirm === password;
  const emailValid = /^\S+@\S+\.\S+$/.test(email.trim());
  const canSubmit = !!name.trim() && emailValid && allRulesMet && passwordsMatch && !isLoading;

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Please enter your name.');

      return;
    }

    if (!emailValid) {
      setError('Please enter a valid email address.');

      return;
    }

    if (!allRulesMet) {
      setError('Please meet all password requirements.');

      return;
    }

    if (!passwordsMatch) {
      setError('Passwords do not match.');

      return;
    }

    register({ email: email.trim(), name: name.trim(), password });
  }

  if (submitted) {
    return (
      <AuthShell>
        <div className={'flex justify-center'}>
          <Logo size={40} />
        </div>

        <div
          className={'mx-auto mt-8 grid h-14 w-14 place-items-center rounded-2xl'}
          style={{ backgroundColor: 'rgba(34,165,68,0.12)' }}
        >
          <MailCheck className={'h-7 w-7'} style={{ color: '#1B752B' }} />
        </div>

        <h1 className={"mt-6 font-['Outfit'] text-2xl font-semibold tracking-[-0.03em]"}>{'Check your email'}</h1>

        <p className={'mx-auto mt-2 max-w-xs text-sm text-neutral-500'}>
          {'We sent a verification link to '}

          <span className={'font-medium text-neutral-800'}>{email.trim()}</span>

          {'. Click it to activate your account.'}
        </p>

        <button
          className={
            'mt-8 h-11 w-full rounded-lg border border-neutral-200 bg-white text-sm font-medium text-neutral-800 transition-colors hover:bg-neutral-50 disabled:opacity-60'
          }
          disabled={resent}
          onClick={() => resend(email.trim())}
          type={'button'}
        >
          {resent ? 'Verification email sent' : 'Resend verification email'}
        </button>

        <p className={'mt-8 text-sm text-neutral-500'}>
          {'Back to '}

          <button
            className={'font-medium text-[#1B752B] hover:underline'}
            onClick={() => navigate(appRoutes.login)}
            type={'button'}
          >
            {'Sign in'}
          </button>
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className={'flex justify-center'}>
        <Logo size={40} />
      </div>

      <h1 className={"mt-8 font-['Outfit'] text-3xl font-semibold tracking-[-0.03em]"}>{'Create your account'}</h1>

      <p className={'mx-auto mt-2 max-w-xs text-sm text-neutral-500'}>
        {'Start seeing your whole financial picture in one place.'}
      </p>

      <form className={'mt-8 space-y-4'} onSubmit={onSubmit}>
        <AuthField autoComplete={'name'} label={'Name'} onChange={setName} type={'text'} value={name} />

        <AuthField autoComplete={'email'} label={'Email'} onChange={setEmail} type={'email'} value={email} />

        <AuthField
          autoComplete={'new-password'}
          label={'Password'}
          onChange={setPassword}
          type={'password'}
          value={password}
        />

        <PasswordRequirements value={password} />

        <AuthField
          autoComplete={'new-password'}
          label={'Confirm password'}
          onChange={setConfirm}
          type={'password'}
          value={confirm}
        />

        {confirm.length > 0 && !passwordsMatch && (
          <p className={'text-left text-xs text-destructive'}>{'Passwords do not match.'}</p>
        )}

        {error && <p className={'text-left text-sm text-destructive'}>{error}</p>}

        <AuthSubmit disabled={!canSubmit}>{isLoading ? 'Creating account…' : 'Create account'}</AuthSubmit>
      </form>

      <p className={'mt-8 text-sm text-neutral-500'}>
        {'Already have an account? '}

        <button
          className={'font-medium text-[#1B752B] hover:underline'}
          onClick={() => navigate(appRoutes.login)}
          type={'button'}
        >
          {'Sign in'}
        </button>
      </p>
    </AuthShell>
  );
}
